import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface VecinosLevel {
  size: number;
  difficulty: number;
  problem: number[];
  solution: number[];
  seed: number;
}

const BULB_STATES = {
  NEUTRAL: -1,
  OFF: 0,
  ON: 1
};

export default function VecinosGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [level, setLevel] = useState<VecinosLevel | null>(null);
  const [userBoard, setUserBoard] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [undoStack, setUndoStack] = useState<number[][]>([]);
  const [redoStack, setRedoStack] = useState<number[][]>([]);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('vecinos', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => {
    loadPuzzle();
  }, [dificultadOrden]);

  const loadPuzzle = async (forceNew = false) => {
    try {
      setLoading(true);
      setError('');

      const urlSeed = !forceNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;

      const response = await api.get('/vecinos/generate', {
        params: {
          dificultad: dificultadOrden,
          ...(seedToUse && { seed: seedToUse })
        }
      });

      const data: VecinosLevel = response.data.data;
      setLevel(data);

      // Initialize user board with all NEUTRAL
      const initialBoard = new Array(data.size * data.size).fill(BULB_STATES.NEUTRAL);
      setUserBoard(initialBoard);

      setMoves(0);
      setSolved(false);
      setShowVictory(false);

      const newUrl = `${window.location.pathname}?seed=${data.seed}`;
      window.history.replaceState({}, '', newUrl);
    } catch (err: any) {
      console.error('Error al cargar puzzle:', err);
      setError(err.response?.data?.error || 'Error al cargar el puzzle');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (index: number) => {
    if (!level || solved) return;

    // Save for undo
    setUndoStack([...undoStack, [...userBoard]]);
    setRedoStack([]);

    const newBoard = [...userBoard];

    // Cycle through states: NEUTRAL → OFF → ON → NEUTRAL
    if (newBoard[index] === BULB_STATES.NEUTRAL) {
      newBoard[index] = BULB_STATES.OFF;
    } else if (newBoard[index] === BULB_STATES.OFF) {
      newBoard[index] = BULB_STATES.ON;
    } else {
      newBoard[index] = BULB_STATES.NEUTRAL;
    }

    setUserBoard(newBoard);
    setMoves(moves + 1);

    // Auto-validate
    setTimeout(() => checkSolution(newBoard), 300);
  };

  const checkSolution = async (board: number[]) => {
    if (!level) return;


    // Don't validate if there are still neutral cells
    if (board.includes(BULB_STATES.NEUTRAL)) {
      return;
    }

    try {
      const response = await api.post('/vecinos/validate', {
        board,
        problem: level.problem,
        size: level.size
      });


      const { valid, errors } = response.data.data;

      if (valid) {
        setSolved(true);
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        await saveProgress(tiempo);
        setShowVictory(true);
      } else {
      }
    } catch (err: any) {
      console.error('💥 Error validando:', err);
      setError('Error al validar la solución');
    }
  };

  const saveProgress = async (tiempo: number) => {
    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: level?.seed || 0,
        tiempo,
        movimientos: moves,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: moves,
        completado: true,
        datos_juego: {
          tablero_id: level?.seed || 0,
          size: level?.size || 0,
        },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, [...userBoard]]);
    setUserBoard(prev);
    setUndoStack(undoStack.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, [...userBoard]]);
    setUserBoard(next);
    setRedoStack(redoStack.slice(0, -1));
  };

  const handleHint = async () => {
    if (!userBoard || !level) return;

    try {
      // Save for undo
      setUndoStack([...undoStack, [...userBoard]]);
      setRedoStack([]);

      const response = await api.post('/vecinos/hint', {
        board: userBoard,
        solution: level.solution,
        size: level.size
      });

      const { hint } = response.data.data;

      if (hint) {
        const newBoard = [...userBoard];
        const index = hint.row * level.size + hint.col;
        newBoard[index] = hint.value;
        setUserBoard(newBoard);
        setMoves(moves + 1);

        setTimeout(() => checkSolution(newBoard), 300);
      }
    } catch (err: any) {
      console.error('Error obteniendo pista:', err);
    }
  };

  const showSolution = () => {
    if (!level || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    setUserBoard([...level.solution]);
    setSolved(true);
  };

  const handleReset = () => {
    if (!level || !window.confirm('¿Estás seguro de que quieres reiniciar?')) return;
    const initialBoard = new Array(level.size * level.size).fill(BULB_STATES.NEUTRAL);
    setUserBoard(initialBoard);
    setMoves(0);
    setSolved(false);
    setShowVictory(false);
    setUndoStack([]);
    setRedoStack([]);
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(board: number[], problem: number[], sz: number): Set<number> {
    const errores = new Set<number>();

    const countLit = (idx: number): number => {
      const r = Math.floor(idx / sz);
      const c = idx % sz;
      let count = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const nr = r + dr, nc = c + dc;
          if (nr >= 0 && nr < sz && nc >= 0 && nc < sz) {
            if (board[nr * sz + nc] === BULB_STATES.ON) count++;
          }
        }
      }
      return count;
    };

    for (let i = 0; i < board.length; i++) {
      if (board[i] === BULB_STATES.NEUTRAL) continue;
      const num = problem[i];
      if (num == null) continue;
      const lit = countLit(i);
      // El número de una bombilla apagada no impone ninguna condición: se ignora.
      if (board[i] === BULB_STATES.ON && lit !== num) errores.add(i);
    }

    return errores;
  }

  const getStateClass = (state: number, hasError: boolean): string => {
    if (hasError) {
      if (state === BULB_STATES.ON) return 'bg-red-200 text-red-700 border-red-400';
      if (state === BULB_STATES.OFF) return 'bg-red-950 text-red-400 border-red-700';
    }
    if (state === BULB_STATES.NEUTRAL) return 'bg-gray-300 text-gray-700 border-gray-400';
    if (state === BULB_STATES.OFF) return 'bg-black text-white border-gray-700';
    if (state === BULB_STATES.ON) return 'bg-white text-black border-gray-200 shadow-[0_0_20px_rgba(255,255,255,0.6)]';
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!level) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const size = level.size;
  const errores = modoAsistido ? calcularErrores(userBoard, level.problem, size) : new Set<number>();

  const difficultyOptions = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-purple-900 to-indigo-900 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">👥 Vecinos</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficultyOptions.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (d.orden === dificultadOrden) loadPuzzle(true);
                else navigate(`/juegos/vecinos/${d.orden}`);
              }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                dificultadOrden === d.orden
                  ? 'bg-white text-purple-900'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Undo */}
          <button onClick={handleUndo} disabled={undoStack.length === 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Redo */}
          <button onClick={handleRedo} disabled={redoStack.length === 0} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Hint */}
          <button onClick={handleHint} disabled={solved} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
          {/* Solution */}
          <button onClick={showSolution} disabled={solved} title="Ver solucion"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* Reset */}
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* New board */}
          <button onClick={() => loadPuzzle(true)} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* How to play */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-red-500/20 border border-red-400/50 text-red-200 rounded text-sm">
          {error}
        </div>
      )}

      {/* Board Area */}
      <div className="vecinos-board flex-1 min-h-0 p-2 overflow-hidden" style={{ display: 'grid', gridTemplateColumns: `repeat(${size}, 1fr)`, gridTemplateRows: `repeat(${size}, 1fr)`, gap: '8px' }}>
          {userBoard.map((state, index) => {
            const number = level.problem[index];
            const isClickable = !solved;
            const hasError = errores.has(index);
            const fontSize = `calc(min((100vw - 16px) / ${size}, (100vh - 126px) / ${size}) * 0.4)`;

            return (
              <button
                key={index}
                onClick={() => handleCellClick(index)}
                disabled={!isClickable}
                className={`
                  rounded-2xl flex items-center justify-center font-bold
                  border-4 transition-all duration-300
                  ${getStateClass(state, hasError)}
                  ${isClickable ? 'cursor-pointer active:scale-95' : 'cursor-default'}
                `}
                style={{ fontSize, userSelect: 'none' }}
              >
                {number}
              </button>
            );
          })}
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Vecinos"
        gameIcon="👥"
        objective="Enciende y apaga bombillas de forma que el número de cada bombilla encendida coincida con el total de bombillas encendidas en su vecindad (las 8 adyacentes + ella misma)."
        rules={[
          'Cada bombilla puede estar encendida (blanca) o apagada (negra)',
          'El número de una bombilla encendida debe coincidir exactamente con el número de bombillas encendidas en su vecindad de 8 celdas, contándose ella misma',
          'El número de una bombilla apagada no importa: se ignora por completo, esa bombilla no impone ninguna condición',
        ]}
        howToPlay="Toca una bombilla para cambiar su estado: neutro → apagada (negra) → encendida (blanca) → neutro. Cuando todos los números de las bombillas encendidas coincidan con su vecindad, habrás resuelto el puzzle."
      />

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
